import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  linkedSignal,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { fileVersionError } from '@modules/files/version-history/file-versions.client';
import { type Subscription } from 'rxjs';
import { type Contact } from '../types/contact';
import { type ContactVersion, ContactVersionsClient, type SavedContactVersion } from './contact-versions.client';

const LOAD_FAILED = 'Version history could not load. Try again.';

type ContactField =
  | 'title'
  | 'jobTitle'
  | 'company'
  | 'address'
  | 'country'
  | 'website'
  | 'birthDate'
  | 'description'
  | 'emails'
  | 'phones';

/** The fields a contact version carries, by the names the contact form shows (#2071's `fields`). */
const FIELDS: ReadonlyArray<readonly [ContactField, string]> = [
  ['title', 'Name'],
  ['jobTitle', 'Job title'],
  ['company', 'Company'],
  ['emails', 'Emails'],
  ['phones', 'Phones'],
  ['address', 'Address'],
  ['country', 'Country'],
  ['website', 'Website'],
  ['birthDate', 'Birthday'],
  ['description', 'Description'],
];
const FIELD_NAMES = new Map<string, string>(FIELDS);

/** One field of a compared version: its saved value beside the contact's current one. */
export interface ContactCompareRow {
  readonly label: string;
  readonly kind: 'text' | 'date';
  readonly saved: string | Date | null;
  readonly current: string | Date | null;
}
type Comparison =
  | { readonly state: 'loading' }
  | { readonly state: 'failed'; readonly message: string }
  | {
      readonly state: 'ready';
      readonly rows: readonly ContactCompareRow[];
    };

/** A team contact's saved versions: compare each with the current contact, and Restore content for an editor. */
@Component({
  selector: 'app-contact-version-history',
  standalone: true,
  imports: [DateFormatPipe],
  // Through this subtree's space-context client, which stamps the contact's own workspace (#2074).
  providers: [ContactVersionsClient],
  templateUrl: './contact-version-history.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactVersionHistoryComponent {
  public readonly contact = input.required<Contact>();
  public readonly canEdit = input(false);
  /** The form is saving or holds unsaved changes: a restore would be overwritten or lost (R4). */
  public readonly held = input(false);
  /** The form takes the restored contact as its current one. */
  public readonly restored = input<(contact: Partial<Contact>) => void>(() => undefined);
  private readonly client = inject(ContactVersionsClient);
  private readonly injector = inject(Injector);
  private readonly space = inject(SpaceContextStore);
  private readonly cancelButton = viewChild<ElementRef<HTMLButtonElement>>('restoreCancel');
  private readonly focusCancel = effect(() => {
    const button = this.cancelButton();
    if (!button) return;
    afterNextRender(
      () => {
        if (this.cancelButton() === button) button.nativeElement.focus();
      },
      { injector: this.injector },
    );
  });
  protected readonly available = linkedSignal(() => {
    const contact = this.contact();
    return !!contact.id && this.space.insideTeam() && contact.accountId === this.space.contextId();
  });
  protected readonly page = linkedSignal(() => {
    this.contact();
    this.space.contextId();
    return 0;
  });
  protected readonly rows = signal<readonly ContactVersion[]>([]);
  protected readonly current = signal<number | null>(null);
  protected readonly total = signal(0);
  protected readonly loading = signal(false);
  protected readonly failure = signal<string | null>(null);
  protected readonly confirmation = signal<number | null>(null);
  protected readonly busy = signal(false);
  protected readonly comparisons = signal<ReadonlyMap<number, Comparison>>(new Map());
  private readonly reread = signal(0);
  private action?: Subscription;
  private generation = 0;

  private readonly identity = effect(() => {
    this.contact();
    this.available();
    this.space.contextId();
    untracked(() => {
      this.generation++;
      this.action?.unsubscribe();
      this.busy.set(false);
      this.confirmation.set(null);
      this.comparisons.set(new Map());
    });
  });
  private readonly load = effect((onCleanup) => {
    const contact = this.contact();
    const available = this.available();
    const offset = this.page() * 25;
    this.reread();
    untracked(() => {
      this.rows.set([]);
      this.total.set(0);
      this.current.set(null);
      this.failure.set(null);
      this.loading.set(available);
    });
    if (!available) return;
    const subscription = this.client.list(contact, offset).subscribe({
      next: (answer) => {
        this.rows.set(answer.items);
        this.current.set(answer.current);
        this.total.set(answer.total);
        this.loading.set(false);
      },
      error: (error: unknown) => {
        this.failure.set(fileVersionError(error, LOAD_FAILED));
        this.loading.set(false);
      },
    });
    onCleanup(() => subscription.unsubscribe());
  });

  public constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.generation++;
      this.action?.unsubscribe();
    });
  }
  protected review(row: ContactVersion): string {
    return row.review === 'requested'
      ? 'Review requested'
      : row.review === 'approved'
        ? 'Approved'
        : 'Changes requested';
  }
  protected changed(row: ContactVersion): string {
    return row.fields.map((field) => FIELD_NAMES.get(field) ?? field).join(', ');
  }
  /** A version's content is read once, the first time its comparison opens. */
  protected compare(version: number, event: Event): void {
    if (!(event.target as HTMLDetailsElement).open || this.comparisons().has(version) || !this.available()) return;
    const contact = this.contact();
    const generation = this.generation;
    this.setComparison(version, { state: 'loading' });
    this.client.read(contact, version).subscribe({
      next: (saved) => {
        if (this.generation === generation)
          this.setComparison(version, { state: 'ready', rows: this.compareRows(saved) });
      },
      error: (error: unknown) => {
        if (this.generation !== generation) return;
        this.setComparison(version, {
          state: 'failed',
          message: fileVersionError(error, 'This version could not load. Try again.'),
        });
      },
    });
  }
  protected comparisonState(version: number): Comparison['state'] | undefined {
    return this.comparisons().get(version)?.state;
  }
  protected comparisonRows(version: number): readonly ContactCompareRow[] {
    const comparison = this.comparisons().get(version);
    return comparison?.state === 'ready' ? comparison.rows : [];
  }
  protected comparisonFailure(version: number): string | null {
    const comparison = this.comparisons().get(version);
    return comparison?.state === 'failed' ? comparison.message : null;
  }
  protected askRestore(version: number): void {
    if (!this.canEdit() || this.busy() || version === this.current()) return;
    this.confirmation.set(version);
    this.failure.set(null);
  }
  protected cancel(): void {
    if (this.busy()) return;
    this.confirmation.set(null);
    this.failure.set(null);
  }
  protected confirmRestore(): void {
    const version = this.confirmation();
    if (version === null || version === this.current() || !this.available() || !this.canEdit()) return;
    if (this.busy() || this.held()) return;
    const contact = this.contact();
    const generation = this.generation;
    this.busy.set(true);
    this.failure.set(null);
    this.action = this.client.restore(contact, version).subscribe({
      next: (answer) => {
        if (this.generation !== generation || this.contact() !== contact) return;
        this.confirmation.set(null);
        this.busy.set(false);
        if (answer.restored !== false) this.restored()(answer.contact);
        this.comparisons.set(new Map());
        this.page.set(0);
        this.reread.update((value) => value + 1);
      },
      error: (error: unknown) => {
        if (this.generation !== generation) return;
        this.failure.set(fileVersionError(error, 'The version could not be restored. Try again.'));
        this.busy.set(false);
      },
    });
  }
  protected retry(): void {
    if (!this.busy()) this.reread.update((value) => value + 1);
  }
  protected previous(): void {
    if (this.busy()) return;
    this.confirmation.set(null);
    this.page.update((page) => Math.max(0, page - 1));
  }
  protected next(): void {
    if (this.busy() || (this.page() + 1) * 25 >= this.total()) return;
    this.confirmation.set(null);
    this.page.update((page) => page + 1);
  }
  private setComparison(version: number, comparison: Comparison): void {
    this.comparisons.update((all) => new Map(all).set(version, comparison));
  }
  private compareRows(saved: SavedContactVersion): ContactCompareRow[] {
    const contact = this.contact();
    return FIELDS.map(([field, label]) => {
      const date = field === 'birthDate';
      return {
        label,
        kind: date ? 'date' : 'text',
        saved: date ? (saved.birthDate ?? null) : this.text(saved[field]),
        current: date ? (contact.birthDate ?? null) : this.text(contact[field]),
      };
    });
  }
  /** Emails and phones as "value (category)", joined; text bound as text. */
  private text(value: unknown): string | null {
    if (Array.isArray(value)) {
      const entries = value
        .filter((entry): entry is { value?: string; category?: string } => !!entry && typeof entry === 'object')
        .filter((entry) => !!entry.value)
        .map((entry) => (entry.category ? `${entry.value} (${entry.category})` : `${entry.value}`));
      return entries.length ? entries.join(', ') : null;
    }
    if (value === null || value === undefined || value === '') return null;
    return String(value);
  }
}
